import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Shield, Zap } from 'lucide-react';
import { cn } from '../utils';

const ActiveParticipants = ({ participants = [], count = 0, variant = 'indigo' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const displayParticipants = Array.isArray(participants) ? participants.slice(0, 5) : [];
  const remainingCount = Math.max(0, (count || participants.length) - displayParticipants.length);

  const colors = {
    indigo: 'bg-brand-indigo text-white',
    red: 'bg-red-500 text-white',
    orange: 'bg-orange-500 text-white',
    green: 'bg-emerald-500 text-white',
  };

  return (
    <div className="relative flex items-center">
      <div 
        className="flex -space-x-3 overflow-hidden cursor-pointer p-1 hover:bg-gray-50 rounded-2xl transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        {displayParticipants.map((p, i) => (
          <motion.div
            key={p.id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.1 }}
            className="relative"
          >
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl border-2 border-white overflow-hidden shadow-sm">
              <img 
                src={p.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${p.name || 'guest'}`} 
                alt={p.name} 
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        ))}
        {remainingCount > 0 && (
          <div className={cn(
            "w-8 h-8 md:w-10 md:h-10 rounded-xl border-2 border-white flex items-center justify-center text-[10px] md:text-xs font-bold shadow-sm relative z-10",
            colors[variant] || colors.indigo
          )}>
            +{remainingCount}
          </div>
        )}
      </div>

      <AnimatePresence>
        {isOpen && (
          <>
            <div 
              className="fixed inset-0 z-[80]" 
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="absolute top-full right-0 mt-3 w-64 bg-white rounded-[2rem] border border-gray-100 shadow-2xl z-[90] p-5 overflow-hidden"
            >
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-50">
                <h4 className="text-xs font-black text-gray-900 uppercase tracking-widest flex items-center gap-2">
                  <Users size={14} className="text-brand-indigo" />
                  Active Now
                </h4>
                <span className="text-[10px] font-bold text-gray-400 bg-gray-50 px-2 py-0.5 rounded-lg">
                  {count || participants.length} online
                </span>
              </div>

              <div className="space-y-3 max-h-[300px] overflow-y-auto no-scrollbar pr-1">
                {participants.length === 0 ? (
                  <p className="text-[11px] font-bold text-gray-400 text-center py-4">No other participants yet.</p>
                ) : (
                  participants.map((p) => (
                    <div key={p.id} className="flex items-center gap-3 group">
                      <div className="w-8 h-8 rounded-xl overflow-hidden border border-gray-100 group-hover:border-brand-indigo/30 transition-colors">
                        <img 
                          src={p.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${p.name || 'guest'}`} 
                          alt={p.name} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-gray-900 truncate">{p.name || 'Guest'}</p>
                        {p.role === 'host' && (
                          <span className="text-[8px] font-bold text-brand-indigo uppercase tracking-widest flex items-center gap-0.5">
                            <Shield size={8} className="fill-current" />
                            Host
                          </span>
                        )}
                      </div>
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                    </div>
                  ))
                )}
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-50 flex items-center justify-center">
                 <p className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.2em] animate-pulse">
                    Live Interaction Active
                 </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ActiveParticipants;
